import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import SectionHelp from '@/components/help/SectionHelp';
import VerticalExampleBox from '@/components/help/VerticalExampleBox';
import HelpTooltip from '@/components/help/HelpTooltip';
import helpContent from '@/help/helpContent';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Wand2, Plus, Play, Eye, Upload, Archive, CheckCircle, Clock,
  ChevronRight, AlertTriangle, X, BookOpen, Brain, Copy, Send, Settings2, Wrench
} from 'lucide-react';

const SKILL_TYPES = [
  'faq_answering', 'lead_qualification', 'objection_handling', 'document_recommendation',
  'conversation_summary', 'intent_classification', 'follow_up_suggestion',
  'handoff_preparation', 'product_explanation', 'service_explanation', 'comparison', 'custom',
];
// 2026-07-06 — these values are stable API keys (don't rename them), but they
// were being shown to the user verbatim in English. This just maps each to a
// Spanish label for display.
const SKILL_TYPE_LABELS = {
  faq_answering: 'Responder preguntas frecuentes', lead_qualification: 'Calificar al prospecto',
  objection_handling: 'Manejar objeciones', document_recommendation: 'Recomendar documento',
  conversation_summary: 'Resumir conversación', intent_classification: 'Clasificar intención',
  follow_up_suggestion: 'Sugerir seguimiento', handoff_preparation: 'Preparar transferencia a humano',
  product_explanation: 'Explicar producto', service_explanation: 'Explicar servicio',
  comparison: 'Comparar opciones', custom: 'Personalizada',
};
const OUTPUT_FORMATS = ['text', 'structured_json', 'bullet_points', 'short_reply'];
const OUTPUT_FORMAT_LABELS = {
  text: 'Texto libre', structured_json: 'Datos estructurados (JSON)',
  bullet_points: 'Lista con viñetas', short_reply: 'Respuesta corta',
};
const RISK_LEVELS = ['low', 'medium', 'high'];
const RISK_LEVEL_LABELS = { low: 'Bajo — solo informa', medium: 'Medio — puede influir en una decisión', high: 'Alto — cerca de dinero o compromisos' };
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700', published: 'bg-emerald-50 text-emerald-700',
  archived: 'bg-gray-100 text-gray-500',
};
// 2026-07-06 — Sprint 38 (resto): matches the channel list Config IA uses
// (AITrainingStudio.js), same reasoning for excluding facebook/instagram.
const SKILL_CHANNELS = ['whatsapp', 'voice', 'email', 'web_chat', 'sms', 'telegram'];
const SKILL_CHANNEL_LABELS = { whatsapp: 'WhatsApp', voice: 'Llamadas', email: 'Email', web_chat: 'Chat web', sms: 'SMS', telegram: 'Telegram' };

export default function AISkillBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState('list');
  const [skills, setSkills] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ key: '', name: '', skill_type: 'custom', prompt_addon: '', expected_output_format: 'text', risk_level: 'low' });
  const [newKeyword, setNewKeyword] = useState('');

  // Simulate
  const [simSkill, setSimSkill] = useState(null);
  const [simMsg, setSimMsg] = useState('');
  const [simResult, setSimResult] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [abResult, setAbResult] = useState(null);
  const [abBusy, setAbBusy] = useState(false);
  const [showAb, setShowAb] = useState(false);

  // 2026-07-25 — S76 (10/N): tres funciones del backend que existían pero no
  // tenían ninguna forma de alcanzarse desde la pantalla: la vista previa de
  // instrucciones, el historial de pruebas y el historial de publicaciones.
  // Vista previa de instrucciones (no llama a la IA, no consume créditos).
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  // Historial de pruebas anteriores (simulaciones guardadas).
  const [histOpen, setHistOpen] = useState(false);
  const [histRows, setHistRows] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histPage, setHistPage] = useState(1);
  const [histMore, setHistMore] = useState(false);
  const [histError, setHistError] = useState(null);

  // Historial de publicaciones de una habilidad (solo lectura).
  const [versionsFor, setVersionsFor] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [sRes, tRes] = await Promise.all([
        api.get('/tenant/ai-skills'),
        api.get('/tenant/ai-skills/templates').catch(() => ({ data: [] })),
      ]);
      setSkills(sRes.data || []);
      setTemplates(tRes.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const prefill = location.state?.skillPrefill;
    if (!prefill) return;
    setEditing(null);
    setForm({
      key: '',
      name: prefill.name || 'Habilidad desde Inbox',
      skill_type: prefill.skill_type || 'faq_answering',
      prompt_addon: prefill.prompt_addon || '',
      expected_output_format: prefill.expected_output_format || 'text',
      risk_level: prefill.risk_level || 'low',
      description: prefill.description || '',
      channels: prefill.channels || ['whatsapp'],
      trigger_keywords: prefill.trigger_keywords || [],
      is_default: false,
    });
    setTab(location.state?.tab || 'edit');
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const handleSave = async () => {
    try {
      if (editing) {
        const { data } = await api.put(`/tenant/ai-skills/${editing}`, form);
        toast.success('Habilidad actualizada');
      } else {
        const { data } = await api.post('/tenant/ai-skills', form);
        toast.success('Habilidad creada');
      }
      setTab('list');
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handlePublish = async (id) => {
    try {
      const { data } = await api.post(`/tenant/ai-skills/${id}/publish`);
      toast.success(`Publicada v${data.version}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleArchive = async (id) => {
    try {
      await api.patch(`/tenant/ai-skills/${id}/archive`);
      toast.success('Archivada');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleClone = async (key) => {
    try {
      const { data } = await api.post(`/tenant/ai-skills/templates/${key}/clone`);
      toast.success(`Template "${data.name}" clonado`);
      load();
      setTab('list');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (skill) => {
    setEditing(skill.id);
    setForm({
      name: skill.name, skill_type: skill.skill_type, prompt_addon: skill.prompt_addon || '',
      expected_output_format: skill.expected_output_format || 'text', risk_level: skill.risk_level || 'low',
      description: skill.description || '',
      channels: skill.channels || [], trigger_keywords: skill.trigger_keywords || [], is_default: !!skill.is_default,
    });
    setTab('edit');
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ key: '', name: '', skill_type: 'custom', prompt_addon: '', expected_output_format: 'text', risk_level: 'low', description: '',
      channels: [], trigger_keywords: [], is_default: false });
    setTab('edit');
  };

  const handleSimulate = async () => {
    if (!simMsg.trim() || !simSkill) return;
    setSimulating(true);
    setSimResult(null);
    try {
      const { data } = await api.post(`/tenant/ai-skills/${simSkill.id}/simulate`, { message: simMsg });
      setSimResult(data);
      // Si el historial de pruebas está abierto, que muestre esta prueba ya.
      if (histOpen) loadHistory(1, false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSimulating(false); }
  };

  const handleCompareAb = async () => {
    if (!simMsg.trim() || !simSkill) return;
    setAbBusy(true);
    setAbResult(null);
    try {
      const { data } = await api.post('/tenant/ai-training/compare-ab', {
        message: simMsg,
        compare_type: 'ai_skill',
        skill_id: simSkill.id,
        variant_a: 'draft',
        variant_b: simSkill.current_version > 0 ? 'published' : 'draft',
      });
      setAbResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAbBusy(false); }
  };

  // Vista previa de las instrucciones finales. El backend no llama a la IA
  // aquí (compone el prompt con include_knowledge=False), así que es gratis.
  const handlePreview = async () => {
    if (!simSkill) return;
    setPreviewBusy(true);
    try {
      const { data } = await api.post(`/tenant/ai-skills/${simSkill.id}/preview`);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No pudimos mostrar las instrucciones. Intenta de nuevo.');
    } finally { setPreviewBusy(false); }
  };

  // OJO: el backend convierte skill_id a UUID sin protección, así que solo se
  // manda un id tomado de la lista de habilidades — nunca texto libre.
  const loadHistory = useCallback(async (page, append) => {
    setHistLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (simSkill?.id) params.skill_id = simSkill.id;
      const { data } = await api.get('/tenant/ai-skills/simulations', { params });
      const rows = Array.isArray(data) ? data : [];
      setHistRows(prev => (append ? [...prev, ...rows] : rows));
      setHistPage(page);
      setHistMore(rows.length === 20);
      setHistError(null);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || 'No pudimos cargar tus pruebas anteriores.';
      setHistError(msg);
      toast.error(msg);
    } finally { setHistLoading(false); }
  }, [simSkill]);

  // Carga perezosa: solo pide datos cuando el usuario abre el historial (y lo
  // refresca si cambia de habilidad con el panel ya abierto).
  useEffect(() => {
    if (!histOpen) return;
    loadHistory(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [histOpen, simSkill?.id]);

  const loadVersions = async (skill) => {
    setVersionsFor(skill.id);
    setVersions([]);
    setVersionsError(null);
    setVersionsLoading(true);
    try {
      const { data } = await api.get(`/tenant/ai-skills/${skill.id}/versions`);
      setVersions(Array.isArray(data) ? data : []);
    } catch (e) {
      setVersionsError(formatApiError(e.response?.data?.detail) || 'No pudimos cargar el historial de publicaciones.');
    } finally { setVersionsLoading(false); }
  };

  const toggleVersions = (skill) => {
    if (versionsFor === skill.id) { setVersionsFor(null); return; }
    loadVersions(skill);
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString('es-MX');
  };

  const tabs = [
    { k: 'list', l: 'Habilidades', icon: Wand2 },
    { k: 'templates', l: 'Templates', icon: Copy },
    { k: 'simulate', l: 'Simulador', icon: Play },
  ];

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-5" data-testid="ai-skill-builder">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
            <Wand2 className="w-5 h-5" /> Constructor de Habilidades
          </h2>
          <p className="text-sm text-[#64748B] mt-0.5">{skills.length} habilidad{skills.length !== 1 ? 'es' : ''} configurada{skills.length !== 1 ? 's' : ''}</p>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="create-skill-btn"><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
      </div>

      <SectionHelp module="skills" title={helpContent.skills.title} description={helpContent.skills.description} whatItDoesNot={helpContent.skills.whatItDoesNot} />
      <VerticalExampleBox module="skills" />

      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 flex items-start gap-2" data-testid="tools-cta-banner">
        <Wrench className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <div>
          Las acciones reales todavía no se ejecutan. Configura herramientas para futuros orquestadores.
          <button onClick={() => navigate('/tenant/ai/tools')} className="underline ml-1 font-medium" data-testid="tools-cta-link">Ir a Herramientas</button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-[#E2E8F0]">
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`skill-tab-${t.k}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.k || (tab === 'edit' && t.k === 'list') ? 'border-[#0F172A] text-[#0F172A]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.l}
          </button>
        ))}
      </div>

      {/* LIST */}
      {tab === 'list' && (
        <div className="space-y-2" data-testid="skill-list">
          {skills.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
              <Wand2 className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
              <p className="text-sm text-[#64748B] mb-3">No hay habilidades creadas. Usa un template o crea una personalizada.</p>
              <div className="flex gap-2 justify-center">
                <Button size="sm" variant="outline" onClick={() => setTab('templates')} data-testid="empty-templates-cta">
                  <Copy className="w-4 h-4 mr-1" /> Ver Templates
                </Button>
                <Button size="sm" onClick={openCreate}><Plus className="w-4 h-4 mr-1" /> Crear</Button>
              </div>
            </div>
          ) : skills.map(s => (
            <div key={s.id} className="bg-white border border-[#E2E8F0] rounded-lg hover:border-[#94A3B8] transition-colors" data-testid={`skill-${s.id}`}>
              <div className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#F8F9FA] flex items-center justify-center flex-shrink-0">
                  <Wand2 className="w-4 h-4 text-[#64748B]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0F172A] truncate">{s.name}</p>
                  <p className="text-xs text-[#94A3B8]">{s.key} · {s.skill_type} {s.current_version > 0 ? `· v${s.current_version}` : ''}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[s.status] || 'bg-gray-100'}`}>{s.status}</span>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(s)} className="p-1.5 hover:bg-[#F8F9FA] rounded" title="Editar"><Settings2 className="w-3.5 h-3.5 text-[#64748B]" /></button>
                  <button onClick={() => { setSimSkill(s); setPreview(null); setTab('simulate'); }} className="p-1.5 hover:bg-[#F8F9FA] rounded" title="Simular"><Play className="w-3.5 h-3.5 text-[#64748B]" /></button>
                  <button onClick={() => toggleVersions(s)} className="p-1.5 hover:bg-[#F8F9FA] rounded" title="Ver historial de publicaciones" data-testid={`skill-versions-toggle-${s.id}`}>
                    <Clock className={`w-3.5 h-3.5 ${versionsFor === s.id ? 'text-[#0F172A]' : 'text-[#64748B]'}`} />
                  </button>
                  {s.status === 'draft' && <button onClick={() => handlePublish(s.id)} className="p-1.5 hover:bg-emerald-50 rounded" title="Publicar"><CheckCircle className="w-3.5 h-3.5 text-emerald-600" /></button>}
                  {s.status !== 'archived' && <button onClick={() => handleArchive(s.id)} className="p-1.5 hover:bg-red-50 rounded" title="Archivar"><Archive className="w-3.5 h-3.5 text-red-400" /></button>}
                </div>
              </div>

              {versionsFor === s.id && (
                <div className="border-t border-[#E2E8F0] bg-[#F8FAFC] px-3 py-3 space-y-2" data-testid={`skill-versions-panel-${s.id}`}>
                  <div className="flex items-center gap-1.5">
                    <p className="text-xs font-semibold text-[#0F172A]">Historial de publicaciones</p>
                    <HelpTooltip text="Cada vez que publicas esta habilidad se guarda una copia de cómo quedó configurada. Aquí ves esas publicaciones, de la más reciente a la más antigua. Por ahora solo puedes consultarlas: todavía no es posible regresar a una versión anterior." />
                  </div>
                  {versionsLoading ? (
                    <div className="flex items-center gap-2 text-[10px] text-[#94A3B8] py-2">
                      <div className="w-3 h-3 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" /> Cargando...
                    </div>
                  ) : versionsError ? (
                    <div className="text-[10px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5">
                      {versionsError}
                      <button onClick={() => loadVersions(s)} className="underline ml-1 font-medium">Reintentar</button>
                    </div>
                  ) : versions.length === 0 ? (
                    <p className="text-[10px] text-[#94A3B8] leading-relaxed">
                      Sin publicaciones todavía. Se crea una cuando presionas el botón de publicar en esta habilidad; mientras tanto tus cambios quedan como borrador.
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {versions.map(v => (
                        <div key={v.id} className="flex items-center gap-2 bg-white border border-[#E2E8F0] rounded px-2 py-1.5" data-testid={`skill-version-${v.version_number}`}>
                          <span className="text-xs font-medium text-[#0F172A]">v{v.version_number}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${v.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                            {v.status === 'published' ? 'Vigente' : 'Reemplazada'}
                          </span>
                          <span className="text-[10px] text-[#94A3B8] ml-auto">{fmtDate(v.published_at)}</span>
                        </div>
                      ))}
                      <p className="text-[10px] text-[#94A3B8] pt-1">Por ahora solo puedes consultar el historial; todavía no es posible regresar a una versión anterior.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* EDIT/CREATE */}
      {tab === 'edit' && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4 max-w-2xl" data-testid="skill-form">
          <h3 className="text-sm font-bold text-[#0F172A]">{editing ? 'Editar Habilidad' : 'Nueva Habilidad'}</h3>
          {!editing && (
            <Input placeholder="Key único (ej: faq_salon)" value={form.key} onChange={e => setForm(f => ({ ...f, key: e.target.value }))} data-testid="skill-key" />
          )}
          <Input placeholder="Nombre" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="skill-name" />
          <Input placeholder="Descripción" value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="skill-description" />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Tipo</label>
              <select value={form.skill_type} onChange={e => setForm(f => ({ ...f, skill_type: e.target.value }))}
                className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid="skill-type">
                {SKILL_TYPES.map(t => <option key={t} value={t}>{SKILL_TYPE_LABELS[t] || t}</option>)}
              </select>
              <p className="text-[10px] text-[#94A3B8] mt-1">Para qué situación de la conversación es esta habilidad.</p>
            </div>
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Formato</label>
              <select value={form.expected_output_format} onChange={e => setForm(f => ({ ...f, expected_output_format: e.target.value }))}
                className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid="skill-format">
                {OUTPUT_FORMATS.map(f => <option key={f} value={f}>{OUTPUT_FORMAT_LABELS[f] || f}</option>)}
              </select>
              <p className="text-[10px] text-[#94A3B8] mt-1">Cómo debe estructurar la IA su respuesta.</p>
            </div>
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Riesgo</label>
              <select value={form.risk_level} onChange={e => setForm(f => ({ ...f, risk_level: e.target.value }))}
                className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid="skill-risk">
                {RISK_LEVELS.map(r => <option key={r} value={r}>{RISK_LEVEL_LABELS[r] || r}</option>)}
              </select>
              <p className="text-[10px] text-[#94A3B8] mt-1">Qué tan delicado es si la IA se equivoca al usar esta habilidad.</p>
            </div>
          </div>
          <div>
            <label className="text-xs text-[#64748B] mb-1 block">Prompt de la habilidad</label>
            <Textarea value={form.prompt_addon} onChange={e => setForm(f => ({ ...f, prompt_addon: e.target.value }))}
              rows={4} placeholder="Instrucciones específicas para esta habilidad..." data-testid="skill-prompt" />
            <p className="text-[10px] text-[#94A3B8] mt-1">{(form.prompt_addon || '').length}/4000</p>
          </div>
          <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg p-3 space-y-3" data-testid="skill-auto-select">
            <div>
              <p className="text-xs font-medium text-[#0F172A] mb-1">¿Cuándo debe usarse esta Habilidad automáticamente?</p>
              <p className="text-[10px] text-[#94A3B8]">Si escribes palabras clave, esta Habilidad se activa cuando el mensaje del cliente contiene alguna de ellas — esto gana siempre, sin importar el canal. Si además la marcas como "por defecto" en un canal, se usa en ese canal cuando ninguna palabra clave de ninguna Habilidad coincidió. Puedes usar ambas a la vez.</p>
            </div>
            <div>
              <label className="text-[10px] text-[#64748B] mb-1 block">Palabras clave disparadoras (opcional)</label>
              <div className="flex gap-1 flex-wrap mb-1">{(form.trigger_keywords || []).map((k, i) => (
                <span key={i} className="text-[10px] bg-[#F1F5F9] border border-[#E2E8F0] rounded-full px-2 py-0.5 flex items-center gap-1">
                  {k} <button type="button" onClick={() => setForm(f => ({ ...f, trigger_keywords: f.trigger_keywords.filter((_, j) => j !== i) }))}><X className="w-2.5 h-2.5" /></button>
                </span>
              ))}</div>
              <div className="flex gap-1">
                <Input value={newKeyword} onChange={e => setNewKeyword(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && newKeyword.trim()) { setForm(f => ({ ...f, trigger_keywords: [...(f.trigger_keywords || []), newKeyword.trim()] })); setNewKeyword(''); } }}
                  placeholder="ej. precio, cancelar, queja..." className="text-xs flex-1" data-testid="skill-keyword-input" />
                <Button size="sm" type="button" onClick={() => { if (newKeyword.trim()) { setForm(f => ({ ...f, trigger_keywords: [...(f.trigger_keywords || []), newKeyword.trim()] })); setNewKeyword(''); } }} className="text-xs bg-[#0F172A]">+</Button>
              </div>
            </div>
            <div>
              <label className="text-[10px] text-[#64748B] mb-1 block">Canales donde aplica (deja vacío para todos)</label>
              <div className="flex flex-wrap gap-2">
                {SKILL_CHANNELS.map(ch => (
                  <label key={ch} className="flex items-center gap-1 text-[10px] text-[#334155]">
                    <input type="checkbox" checked={(form.channels || []).includes(ch)}
                      onChange={e => setForm(f => ({ ...f, channels: e.target.checked ? [...(f.channels || []), ch] : (f.channels || []).filter(c => c !== ch) }))}
                      className="w-3 h-3" data-testid={`skill-channel-${ch}`} />
                    {SKILL_CHANNEL_LABELS[ch]}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-[#334155]">
              <input type="checkbox" checked={form.is_default || false}
                onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))}
                className="w-3.5 h-3.5" data-testid="skill-is-default" />
              Usar como Habilidad por defecto en esos canales (si otra ya era la de defecto, deja de serlo)
            </label>
          </div>
          <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.knowledge_scope?.enabled || false}
                onChange={e => setForm(f => ({ ...f, knowledge_scope: { ...(f.knowledge_scope || {}), enabled: e.target.checked, mode: 'hybrid', max_chunks: 5 } }))}
                className="w-3.5 h-3.5" data-testid="skill-knowledge-toggle" />
              <label className="text-xs font-medium text-[#0F172A] flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> Usar Biblioteca de Conocimiento</label>
            </div>
            <p className="text-[10px] text-[#94A3B8] pl-5">Activa esto si la habilidad debe consultar tus documentos subidos (precios, catálogo, políticas) en vez de responder solo con lo que ya sabe la IA.</p>
            {form.knowledge_scope?.enabled && (
              <div className="grid grid-cols-2 gap-2 pl-5">
                <div>
                  <label className="text-[10px] text-[#94A3B8]">Modo búsqueda</label>
                  <select value={form.knowledge_scope?.mode || 'hybrid'}
                    onChange={e => setForm(f => ({ ...f, knowledge_scope: { ...f.knowledge_scope, mode: e.target.value } }))}
                    className="w-full text-xs border border-[#E2E8F0] rounded px-2 py-1" data-testid="skill-ks-mode">
                    <option value="keyword">Solo palabras clave</option>
                    <option value="hybrid">Híbrido (recomendado)</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-[#94A3B8]">Máx. fragmentos a usar</label>
                  <Input type="number" min={1} max={10} value={form.knowledge_scope?.max_chunks || 5}
                    onChange={e => setForm(f => ({ ...f, knowledge_scope: { ...f.knowledge_scope, max_chunks: parseInt(e.target.value) || 5 } }))}
                    className="text-xs h-7" data-testid="skill-ks-chunks" />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-1.5 text-[10px] text-[#64748B]">
                    <input type="checkbox" checked={form.knowledge_scope?.require_sources || false}
                      onChange={e => setForm(f => ({ ...f, knowledge_scope: { ...f.knowledge_scope, require_sources: e.target.checked } }))}
                      className="w-3 h-3" data-testid="skill-ks-sources" />
                    Citar fuentes en respuesta
                  </label>
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => { setTab('list'); setEditing(null); }}>Cancelar</Button>
            <Button size="sm" onClick={handleSave} data-testid="skill-save">{editing ? 'Actualizar' : 'Crear'}</Button>
          </div>
        </div>
      )}

      {/* TEMPLATES */}
      {tab === 'templates' && (
        <div className="space-y-2" data-testid="skill-templates">
          {templates.length === 0 ? (
            <p className="text-sm text-[#94A3B8] text-center py-8">No hay templates disponibles</p>
          ) : templates.map(t => (
            <div key={t.key} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3" data-testid={`template-${t.key}`}>
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Copy className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0F172A]">{t.name}</p>
                <p className="text-xs text-[#94A3B8]">{t.skill_type} · {t.description?.substring(0, 60)}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => handleClone(t.key)} className="text-xs" data-testid={`clone-${t.key}`}>
                <Copy className="w-3 h-3 mr-1" /> Clonar
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* SIMULATOR */}
      {tab === 'simulate' && (
        <div className="space-y-4 max-w-2xl" data-testid="skill-simulator">
          <div>
            <label className="text-xs text-[#64748B] mb-1 block">Habilidad</label>
            <select value={simSkill?.id || ''} onChange={e => { setSimSkill(skills.find(s => s.id === e.target.value) || null); setPreview(null); }}
              className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid="sim-skill-select">
              <option value="">Seleccionar habilidad...</option>
              {skills.filter(s => s.status !== 'archived').map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.key})</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <Textarea placeholder="Mensaje de prueba..." value={simMsg} onChange={e => setSimMsg(e.target.value)} rows={2} className="flex-1" data-testid="sim-message" />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSimulate} disabled={simulating || !simSkill || !simMsg.trim()} className="flex-1" data-testid="sim-submit">
              {simulating ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {simulating ? 'Simulando...' : 'Simular habilidad'}
            </Button>
            <Button variant="outline" onClick={handlePreview} disabled={previewBusy || !simSkill} className="text-xs gap-1" data-testid="skill-preview-btn">
              <Eye className="w-3.5 h-3.5" /> {previewBusy ? 'Preparando...' : 'Ver instrucciones'}
            </Button>
            <Button variant="outline" onClick={() => setShowAb(v => !v)} disabled={!simSkill} className="text-xs" data-testid="skill-ab-toggle">
              Comparar A/B
            </Button>
          </div>
          <p className="text-[10px] text-[#94A3B8] -mt-2">
            "Ver instrucciones" te muestra las indicaciones completas que recibirá la IA con esta habilidad. No le pregunta nada a la IA, así que no consume créditos.
          </p>

          {preview && (
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4 space-y-3" data-testid="skill-preview-panel">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-semibold text-[#0F172A]">Instrucciones de "{preview.skill_name}"</p>
                  <HelpTooltip text="Es el texto completo que la IA lee antes de responder: la forma de hablar de tu negocio, las reglas del canal y lo que escribiste en esta habilidad, todo junto. Sirve para revisar si falta algo antes de publicar." />
                </div>
                <button onClick={() => setPreview(null)} title="Cerrar" data-testid="skill-preview-close"><X className="w-3.5 h-3.5 text-[#94A3B8]" /></button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${preview.channel_rules_applied ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                  Reglas del canal: {preview.channel_rules_applied ? 'sí se aplican' : 'no se aplican'}
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#F1F5F9] text-[#334155]">
                  Respuesta esperada: {OUTPUT_FORMAT_LABELS[preview.expected_output_format] || preview.expected_output_format || '—'}
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#F1F5F9] text-[#334155]">
                  Tipo: {SKILL_TYPE_LABELS[preview.skill_type] || preview.skill_type}
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#F1F5F9] text-[#334155]">
                  Documentos: {preview.knowledge_scope?.enabled ? 'sí los consulta' : 'no los consulta'}
                </span>
              </div>
              {preview.warnings?.length > 0 && (
                <div className="space-y-1">
                  {preview.warnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" /> {w}
                    </div>
                  ))}
                </div>
              )}
              {(preview.objectives?.length > 0 || preview.guardrails?.length > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {preview.objectives?.length > 0 && (
                    <div className="bg-white border border-[#E2E8F0] rounded-lg p-2">
                      <p className="text-[10px] font-semibold text-[#64748B] mb-1">Qué debe lograr</p>
                      <ul className="text-[10px] text-[#334155] list-disc pl-4 space-y-0.5">{preview.objectives.map((o, i) => <li key={i}>{o}</li>)}</ul>
                    </div>
                  )}
                  {preview.guardrails?.length > 0 && (
                    <div className="bg-white border border-[#E2E8F0] rounded-lg p-2">
                      <p className="text-[10px] font-semibold text-[#64748B] mb-1">Qué no debe hacer</p>
                      <ul className="text-[10px] text-[#334155] list-disc pl-4 space-y-0.5">{preview.guardrails.map((g, i) => <li key={i}>{g}</li>)}</ul>
                    </div>
                  )}
                </div>
              )}
              <div>
                <p className="text-[10px] font-semibold text-[#64748B] mb-1">Texto completo que recibe la IA</p>
                <pre className="text-[11px] text-[#0F172A] whitespace-pre-wrap font-mono bg-white border border-[#E2E8F0] rounded-lg p-3 max-h-72 overflow-y-auto" data-testid="skill-preview-text">
                  {preview.final_system_prompt_preview || 'Sin contenido.'}
                </pre>
                <p className="text-[10px] text-[#94A3B8] mt-1">Se muestran los primeros 2,000 caracteres.</p>
              </div>
            </div>
          )}
          {showAb && (
            <div className="border border-[#E2E8F0] rounded-lg p-3 space-y-2 bg-[#F8FAFC]" data-testid="skill-ab-panel">
              <p className="text-[10px] text-[#64748B]">Compara el borrador actual de esta habilidad contra la versión publicada con el mismo mensaje de prueba.</p>
              <Button size="sm" onClick={handleCompareAb} disabled={abBusy || !simMsg.trim() || !simSkill} data-testid="skill-ab-run">
                {abBusy ? 'Comparando...' : 'Ejecutar comparación A/B'}
              </Button>
              {abResult?.variant_a && abResult?.variant_b && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                  {[abResult.variant_a, abResult.variant_b].map((v) => (
                    <div key={v.side} className="bg-white border border-[#E2E8F0] rounded-lg p-3" data-testid={`skill-ab-${v.side}`}>
                      <p className="text-[10px] font-semibold text-[#64748B] mb-1">{v.label}</p>
                      <p className="text-xs text-[#0F172A] whitespace-pre-wrap">{v.text || v.error || '—'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {simResult && (
            <div className="space-y-3" data-testid="sim-result">
              {simResult.warnings?.map((w, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {w}
                </div>
              ))}
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Brain className="w-4 h-4 text-[#F72585]" />
                  <span className="text-xs font-medium text-[#0F172A]">Respuesta ({simResult.skill_name})</span>
                  {simResult.latency_ms > 0 && <span className="text-[10px] text-[#94A3B8]">{simResult.latency_ms}ms</span>}
                </div>
                <p className="text-sm text-[#334155] whitespace-pre-wrap">{simResult.response}</p>
              </div>
              {simResult.sources?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-[#64748B] mb-1">Fuentes:</p>
                  {simResult.sources.map((s, i) => (
                    <div key={i} className="text-xs text-[#334155] bg-[#F8F9FA] rounded-lg p-2 mb-1">
                      <span className="font-medium">{s.title}</span> — {s.source_reference}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Historial de pruebas anteriores */}
          <div className="bg-white border border-[#E2E8F0] rounded-xl" data-testid="skill-sim-history">
            <button type="button" onClick={() => setHistOpen(v => !v)}
              className="w-full flex items-center gap-2 px-4 py-3 text-left" data-testid="skill-sim-history-toggle">
              <ChevronRight className={`w-3.5 h-3.5 text-[#64748B] transition-transform ${histOpen ? 'rotate-90' : ''}`} />
              <Clock className="w-3.5 h-3.5 text-[#64748B]" />
              <span className="text-xs font-semibold text-[#0F172A]">Historial de pruebas</span>
              <span className="text-[10px] text-[#94A3B8] ml-auto">{simSkill ? `Solo "${simSkill.name}"` : 'Todas las habilidades'}</span>
            </button>
            {histOpen && (
              <div className="border-t border-[#E2E8F0] px-4 py-3 space-y-2">
                <p className="text-[10px] text-[#94A3B8] leading-relaxed">
                  Aquí quedan guardadas tus pruebas anteriores para comparar cómo respondía la habilidad antes y después de editarla. Solo se guardan las pruebas que la IA logró responder.
                </p>
                {histLoading && histRows.length === 0 ? (
                  <div className="flex items-center gap-2 text-[10px] text-[#94A3B8] py-3">
                    <div className="w-3 h-3 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" /> Cargando...
                  </div>
                ) : histError ? (
                  <div className="text-[10px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5">
                    {histError}
                    <button onClick={() => loadHistory(1, false)} className="underline ml-1 font-medium" data-testid="skill-sim-history-retry">Reintentar</button>
                  </div>
                ) : histRows.length === 0 ? (
                  <p className="text-[10px] text-[#94A3B8] py-2">Sin pruebas todavía. Escribe un mensaje arriba y presiona "Simular habilidad".</p>
                ) : (
                  <>
                    <div className="space-y-2">
                      {histRows.map(h => (
                        <div key={h.id} className="border border-[#E2E8F0] rounded-lg p-2.5 bg-[#F8FAFC]" data-testid={`skill-sim-history-row-${h.id}`}>
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-[10px] text-[#64748B]">{fmtDate(h.created_at)}</span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#334155]">
                              {SKILL_CHANNEL_LABELS[h.channel] || h.channel || 'Sin canal'}
                            </span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                              {h.status === 'success' ? 'Respondió bien' : (h.status || '—')}
                            </span>
                            {h.latency_ms > 0 && <span className="text-[10px] text-[#94A3B8] ml-auto">Tardó {h.latency_ms} ms</span>}
                          </div>
                          <p className="text-[11px] text-[#64748B]"><span className="font-medium text-[#334155]">Tu mensaje:</span> {h.user_message_preview || '—'}</p>
                          <p className="text-[11px] text-[#334155] whitespace-pre-wrap mt-1"><span className="font-medium">Respuesta:</span> {h.response_preview || '—'}</p>
                        </div>
                      ))}
                    </div>
                    {histMore && (
                      <Button size="sm" variant="outline" className="text-xs w-full" disabled={histLoading}
                        onClick={() => loadHistory(histPage + 1, true)} data-testid="skill-sim-history-more">
                        {histLoading ? 'Cargando...' : 'Cargar más'}
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
